import type { EdgeDescriptor } from '@bliks-io/ui';
import {
  currentSuccessorStatus,
  detachEventFromStep,
  findStepByStableId,
  rulesPatchWithSuccessorStatus,
  taskPatchWithSuccessorStatus,
  collectDownstreamOf,
  upgradeLegacyEventInput
} from './shared';
import type { EdgeActionDeps } from './types';

/**
 * Disconnecting the start event from a node routes through the detach
 * endpoint — the backend clears the `eventId` link, `previousStep`, and any
 * legacy START-tag form in one call. Undo is the server-side draft history.
 */
async function disconnectStartFromStep(
  startEventId: string,
  targetStepId: string,
  deps: EdgeActionDeps
): Promise<void> {
  await detachEventFromStep(startEventId, targetStepId, deps);
}

async function disconnectStepFromEnd(
  sourceStepId: string,
  ruleId: string | undefined,
  deps: EdgeActionDeps
): Promise<void> {
  const step = findStepByStableId(deps.getCachedSteps(), sourceStepId);
  if (!step) return;
  const prevStatus = currentSuccessorStatus(step, ruleId);
  if (prevStatus !== 'NONE' && prevStatus !== 'UNCLEAR') return;
  const forward = ruleId
    ? rulesPatchWithSuccessorStatus(step, ruleId, '')
    : taskPatchWithSuccessorStatus(step, '');
  await deps.updateStep({ stepId: sourceStepId, patch: forward });
}

async function disconnectTaskForkBeforeGateway(
  sourceStepId: string,
  deps: EdgeActionDeps
): Promise<void> {
  const allSteps = deps.getCachedSteps();
  const source = findStepByStableId(allSteps, sourceStepId);
  if (!source) return;
  const captured = collectDownstreamOf(String(source.stableStepId), allSteps, sourceStepId);
  if (captured.length === 0) return;

  for (const { targetId } of captured) {
    await deps.disconnect(sourceStepId, targetId);
  }
}

async function disconnectStepToStep(
  sourceStepId: string,
  targetStepId: string,
  ruleId: string | undefined,
  deps: EdgeActionDeps
): Promise<void> {
  // `ruleId` is the clicked output's representative (from `rule-<rep>`): scope
  // removal to that output so a sibling output of the same decision survives.
  await deps.disconnect(sourceStepId, targetStepId, ruleId);
}

export async function performDisconnect(edge: EdgeDescriptor, deps: EdgeActionDeps): Promise<void> {
  const { source, target } = edge;

  if (source.kind === 'start' && target.kind === 'step' && target.stepId) {
    // The start event is a real event node (eventId = its node id). The
    // legacy synthesized `start-node` carries no eventId — its edges can no
    // longer exist in the draft editor, so a missing id is a no-op.
    if (source.eventId) {
      await disconnectStartFromStep(source.eventId, target.stepId, deps);
    }
    return;
  }

  if (source.kind === 'event' && target.kind === 'step' && target.stepId) {
    // Canonical event? Detach directly. Legacy event input (no `eventId` on
    // the edge descriptor — its node id is `event-<stepId>-<inputIdx>`) gets
    // upgraded server-side first so the detach runs against the new
    // canonical eventId.
    let eventId = source.eventId ?? null;
    if (!eventId && source.nodeId) {
      eventId = await upgradeLegacyEventInput(source.nodeId, deps);
    }
    if (!eventId) return;
    await detachEventFromStep(eventId, target.stepId, deps);
    return;
  }

  if (target.kind === 'end' && source.kind === 'step' && source.stepId) {
    await disconnectStepFromEnd(source.stepId, edge.ruleId, deps);
    return;
  }

  if (edge.taskForkRole === 'beforeFork' && source.stepId) {
    await disconnectTaskForkBeforeGateway(source.stepId, deps);
    return;
  }

  if (source.stepId && target.stepId && source.stepId !== target.stepId) {
    await disconnectStepToStep(source.stepId, target.stepId, edge.ruleId, deps);
  }
}
